import styled from '@emotion/styled';

// Main container components
//
// No fixed height any more: the old `height: calc(100vh - 100px)` assumed a
// 100px header and a desktop viewport, which on a phone left the body a few
// scrollable pixels tall. The editor now grows with its content and the page
// scrolls, which is also what makes the on-screen keyboard behave.
export const EditorContainer = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    padding: 0.75rem;
    box-sizing: border-box;

    @media (min-width: 900px) {
        padding: 1.5rem;
    }
`;

export const EditorSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    min-width: 0;
    > * {
        max-width: 100%;
        box-sizing: border-box;
    }
`;

// Form components
export const TitleInput = styled.input`
    width: 100%;
    padding: 0.6rem 0.75rem;
    /* 16px minimum, or iOS Safari zooms the viewport on focus. */
    font-size: 1.25rem;
    font-weight: 600;
    border: 1px solid #ddd;
    border-radius: 10px;
    box-sizing: border-box;
    background: #fff;

    &:focus {
        outline: 2px solid #191716;
        outline-offset: -1px;
    }

    @media (min-width: 900px) {
        font-size: 1.5rem;
    }
`;

export const EditorWrapper = styled.div`
    /* A comfortable writing area that grows with the text instead of being a
       fixed box with its own scrollbar — nested scroll areas are unusable on
       touch, where the outer page and the inner box fight over the gesture. */
    min-height: 45vh;
    border: 1px solid #ddd;
    border-radius: 10px;
    padding: 1rem;
    background-color: white;
    width: 100%;
    box-sizing: border-box;
    line-height: 1.6;

    .DraftEditor-root {
        width: 100%;
    }
    .DraftEditor-editorContainer {
        width: 100%;
    }
    /* Draft renders block elements verbatim; give the article shapes the same
       rhythm they have on the site so the editor previews the result. */
    h2 {
        font-size: 1.35rem;
        margin: 1.2rem 0 0.4rem;
    }
    h3 {
        font-size: 1.15rem;
        margin: 1rem 0 0.3rem;
    }
    figure,
    img,
    iframe {
        max-width: 100%;
    }
`;

// Toolbar components
//
// Sticky, so the formatting controls stay reachable while writing a long body
// on a phone — the old toolbar scrolled away after the first paragraph. It
// wraps rather than overflowing: ten buttons never fit one phone-width row.
export const Toolbar = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    width: 100%;
    align-items: center;

    .button-group {
        display: flex;
        gap: 0.35rem;
    }
`;

export const ToolbarButton = styled.button`
    padding: 0.5rem;
    background: ${(props) => (props.variant === 'primary' ? '#191716' : props.active ? '#e0e2db' : 'white')};
    color: ${(props) => (props.variant === 'primary' ? 'white' : '#495057')};
    border: 1px solid ${(props) => (props.variant === 'primary' ? '#191716' : props.active ? '#b9bcb2' : '#dee2e6')};
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    font-family: inherit;
    transition: all 0.15s ease;
    /* 44px is the smallest comfortable touch target. */
    min-width: 44px;
    min-height: 44px;
    justify-content: center;

    &:hover {
        background: ${(props) => (props.variant === 'primary' ? '#000' : props.active ? '#d3d6cb' : '#f1f3f5')};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    svg {
        font-size: 1.05rem;
    }

    &.media-button {
        padding: 0.5rem 0.75rem;
    }

    /* Labels next to the icons are desktop-only; on a phone the icon carries
       the meaning and the row stays one line shorter. */
    .label {
        display: none;
    }
    @media (min-width: 600px) {
        .label {
            display: inline;
        }
    }
`;
